import axios from "axios"
import store from "../store/store"
import { showAlert } from "../reducer/alertSlice"
import { setToken } from "../reducer/authSlice"
import { refresh } from "./auth/service"

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL + "api/",
    timeout: 10 * 1000,
    withCredentials: true,  
})

// 로컬에 저장된 accessToken을 헤더에 삽입 (요청이 생길때마다 호출 됨)
api.interceptors.request.use(
    config => {
        const token = store.getState().auth.token
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    error => Promise.reject(error)
)

// 공통 에러 핸들링
api.interceptors.response.use(
    response => response,
    async error => {
        // 네트워크 단계 에러 (서버 응답 자체가 없을 때)
        if (!error.response) {
            // Node 환경이면 error.code === 'ECONNREFUSED' 로 올 것이다.
            if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
                console.error("@apiClient - Error: Connection Refused", error)
                store.dispatch(showAlert({
                    msg: '서버에 연결할 수 없습니다.\n잠시 후 다시 시도해주세요.'
                }))
                return Promise.reject(error)
            }
        }
        // 타임아웃
        if (error.code === "ECONNABORTED") {
            console.error("@apiClient - Error: Timeout", error)
            store.dispatch(showAlert({
                msg: "요청 시간이 초과되었습니다.\n인터넷 연결을 확인하고 다시 시도해주세요.",
            }))
        }

        const status = error.response?.status
        const originalRequest = error.config

        // 401(인증만료) 상태 값이 올 경우
        if (status === 401) {
            // 로그인 화면일 때 (ID/PW 틀렸을 경우 등)
            if (originalRequest.url.includes('/auth/login')) {
                console.log("@apiClient: satus 401 && isLoginPage = Unauthorized", error)
                // 여기서 아무 처리도 하지 않고 로그인(/) 페이지에서 핸들링 하도록 흐름 넘김
                // 이 return이 꼭 필요한 건 아니지만 로직 흐름 가독성을 위해 명시적으호 호출
                return Promise.reject(error)    
            }
            // 로그인 외의 화면일 때 (토큰 만료 등)
            else {
                // Refresh 응답으로 다시 401 상태를 받았을 때 = Refresh Token 만료 (최초 status: 401 -> refresh -> 다시 status: 401)
                // or _retry = true: 이미 refresh 시도를 한 요청으로 판단
                // 무한루프 방지 차원으로 이 블록에 흐름 들어오도록. 이 블록에선 reject() 만 하고 실제 로그아웃 처리는 원래 호출의 try{}-catch{} 문에 맡긴다.
                if (originalRequest.url.includes('/auth/refresh') || originalRequest._retry) {
                    return Promise.reject(error)
                }
                console.log("@apiClient: status 401 && !isLoginPage -> Refresh", error)

                // 무한루프 방지용 가드
                originalRequest._retry = true

                // 첫 401 응답시 Refresh 수행
                try {
                    const result = await refresh()
                    const newToken = result.data.access_token
                    // 여기서 refresh() 호출 성공시엔 if 문 내부 로직 수행, 실패시엔 error를 intercept 하여 status == 401 분기문을 다시 수행
                    if (result.code === 0 && newToken) {
                        console.log("@apiClient: Refresh Success")
                        // 새 토큰 저장 후 원래 요청 재시도
                        store.dispatch(setToken(newToken))
                        return api(originalRequest)
                    }
                } catch (e) {
                    // Refresh 호출 자체가 실패했을 때 (혹은 Refresh 마저 401 응답을 받았을 때)
                }
                console.log("@apiClient: Refresh Token Expried", error)
                store.dispatch(
                showAlert({
                    msg: "로그인 후 이용해주세요.",
                    func: () => {
                        window.location.href = "/auth/logout"
                    }
                })
            )
            }
        }
        // 유저 등급(권한)에 맞지 않는 요청을 수행
        else if (status == 403) {
            console.error("@apiClient (400 ~)", error)
            store.dispatch(showAlert({
                msg: "요청을 실행할 권한이 없습니다. (403)",
            }))
        }
        // 400번대 에러
        else if (status >= 400 && status < 500) {
            console.error("@apiClient (400 ~)", error)
            store.dispatch(showAlert({
                msg: "잘못된 요청입니다. (400 ~)",
            }))
        } 
        // 500 에러
        else if (status === 500) {
            console.error("@apiClient (500)", error)
            store.dispatch(showAlert({
                msg: "서버에 문제가 발생했습니다. (500)",
            }))
        }
        return Promise.reject(error)
    }
)

export default api